// pages/EditUser.tsx — FINAL RTP FULLY INTEGRATED VERSION
// Real-time notify, RTP warnings, toast feedback, optimistic UI

import { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast (if not already)

interface FormData {
  name: string;
  phone: string;
  email: string;
  password?: string;
  mode: 'cash';
  managerId: string;
}

interface Manager {
  id: string;
  login_id?: string;
  name: string;
  email?: string;
}

interface User {
  id: string;
  login_id?: string;
  email: string;
  name: string;
  phone: string;
  mode: 'cash';
  manager_id?: string;
}

export default function EditUser() {
  const [user, setUser] = useState<User | null>(null);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { token, refreshCsrf, error, setError, socket, fetchManagers } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();
  const location = useLocation();
  const navigate = useNavigate();
  const identifier = new URLSearchParams(location.search).get('id')
    || new URLSearchParams(location.search).get('email');

  const fetchUser = useCallback(async (identifier: string) => {
    try {
      const res = await fetch(`/admin/user/${encodeURIComponent(identifier)}`, {
        headers: { Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        reset({
          name: data.name,
          phone: data.phone,
          email: data.email,
          mode: data.mode,
          managerId: data.manager_id || '',
        });
      } else {
        setError('Failed to load user');
      }
    } catch {
      setError('Failed to load user');
    } finally {
      setInitialLoading(false);
    }
  }, [token, reset, setError]);

  useEffect(() => {
    if (identifier) {
      fetchUser(identifier);
      fetchManagers().then(data => setManagers((data || []) as any[]));
      refreshCsrf();
      return;
    }
    setInitialLoading(false);
  }, [identifier, token, refreshCsrf, fetchManagers, fetchUser]);

  // Real-time user updated listener (SAFE & LEAK-FREE)
  useEffect(() => {
    if (!socket) return;

    const handleUserUpdated = (data: { userId?: string; email: string; mode: string }) => {
      toast.success(`User updated: ${data.email}`);

      // Re-fetch current user only if same user is being edited
      const currentEmail = new URLSearchParams(location.search).get('email');
      const currentId = new URLSearchParams(location.search).get('id') || currentEmail;
      if (currentId && (currentId === data.userId || currentEmail === data.email)) {
        fetchUser(currentId);
      }
    };

    socket.on('user-updated', handleUserUpdated);

    return () => {
      socket.off('user-updated', handleUserUpdated);
    };
  }, [socket, location, fetchUser]);


  const onSubmit = async (data: FormData) => {
    if (!user) {
      setError('User data not loaded');
      return;
    }

    const currentUser = user;

    setLoading(true);
    setError('');

    try {
      const csrfToken = await refreshCsrf();
      const sanitizedData = {
        userId: currentUser.id,
        login_id: currentUser.login_id,
        name: DOMPurify.sanitize(data.name.trim()),
        phone: DOMPurify.sanitize((data.phone || "").trim()),
        password: data.password || undefined,
        mode: 'cash',
        managerId: data.managerId || null,
      };

      const res = await fetch('/admin/update-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error?.message || 'Failed to update user');
      }

      toast.success(`User updated successfully! User: ${currentUser.login_id || currentUser.email}`);
      navigate('/admin/dashboard?section=manage-users');
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    } 
  };

  if (initialLoading) return <div className="p-6 text-center text-gray-700">Loading user data...</div>;
  if (!identifier) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Select a User to Edit</h2>
          <p className="text-gray-600 mb-6">
            Edit User direct menu se open karne par koi user select nahi hota. User ko Manage Users list se choose kijiye.
          </p>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard?section=manage-users')}
            className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
          >
            Go to Manage Users
          </button>
        </div>
      </div>
    );
  }
  if (!user) return <div className="p-6 text-center text-red-600">User not found.</div>;

  const currentUser = user;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Edit User: {currentUser.login_id || currentUser.name}</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <input type="hidden" name="_csrf" />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('phone', { required: 'Phone is required' })}
            />
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address (Read-only)
            </label>
            <input
              type="email"
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              value={currentUser.login_id || currentUser.email || ""}
              readOnly
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password (Optional)
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 pr-12"
              placeholder="Leave blank to keep current"
              {...register('password', {
                minLength: { value: 8, message: 'Min 8 characters' },
              })}
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-600 hover:text-gray-800"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              value="Cash Mode"
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign Manager (Optional)
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('managerId')}
            >
              <option value="">No Manager</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>
                  {DOMPurify.sanitize(`${m.name} (${m.login_id || m.email || ""}`)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={loading || !(token || localStorage.getItem('adminToken'))}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${
              loading || !(token || localStorage.getItem('adminToken'))
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Updating...
              </span>
            ) : (
              'Update User'
            )}
          </button>
        </form>
      </div>

      {/* RTP Note */}
      <p className="mt-6 text-sm text-gray-500 text-center">
        Demo mode is disabled for managed users. Cash access is always enabled now.
      </p>
    </div>
  );
}
